/**
 * Column Resolvers for Custom Report Builder
 * Resolves column values from different source types
 */
import { FormulaEngine } from './FormulaEngine.js';
import { buildCalculatorInput } from '../calculator-input-builder.js';
import { CalculationService } from '../../services/CalculationService.js';
import { CalculatorRegistry } from '../../calculators/CalculatorRegistry.js';
import { PrivateCompany409ACalculator } from '../../calculators/PrivateCompany409ACalculator.js';
import { FundInvestorCalculator } from '../../calculators/FundInvestorCalculator.js';
import { ContributionsAccrualsCalculator } from '../../calculators/ContributionsAccrualsCalculator.js';
import { ValuationPlusBasisCalculator } from '../../calculators/ValuationPlusBasisCalculator.js';
import { TvpiFundCalculator } from '../../calculators/TvpiFundCalculator.js';
import { IncomeAllocationsCalculator } from '../../calculators/IncomeAllocationsCalculator.js';

// Lazy-initialized calculator service for report resolvers
let _calculationService = null;
function getCalculationService() {
	if (!_calculationService) {
		const registry = new CalculatorRegistry();
		registry
			.register(PrivateCompany409ACalculator)
			.register(FundInvestorCalculator)
			.register(ContributionsAccrualsCalculator)
			.register(ValuationPlusBasisCalculator)
			.register(TvpiFundCalculator)
			.register(IncomeAllocationsCalculator);
		_calculationService = new CalculationService(registry, {});
	}
	return _calculationService;
}

/**
 * Ensure calculator results are attached to the context as `context.calc`.
 * Lazily computed and cached — runs the same dashboard calculators used on
 * the investor portal, so any field the calculator produces is accessible
 * via dot notation (e.g., calc.totalValue, calc.classes.ClassName.value).
 *
 * The `calc.classes` object re-keys the breakdown array by classKey for
 * easy dot-notation access in formulas.
 *
 * @param {Object} context - Shareholder context
 */
function ensureCalcResult(context) {
	if (context.calc !== undefined) return;

	const { userClient, clientData, shareClasses, userid, capTableData } = context;
	if (!userClient?.shares || !clientData?.equity) {
		context.calc = null;
		return;
	}

	const classes = shareClasses || Object.keys(clientData.equity);
	const clientKey = context.clientKey || (typeof client !== 'undefined' ? client : '');
	const input = buildCalculatorInput({
		userShares: userClient.shares,
		clientData,
		shareClasses: classes,
		userIds: userid,
		options: { clientFlags: { isUA: clientKey === 'urbanalpha', isCER: clientKey === 'ceritypartners' } },
		capTableData,
		client: clientKey,
	});

	if (!input) {
		context.calc = null;
		return;
	}

	const service = getCalculationService();
	const calculator = service.getCalculatorForClient(clientData);
	const result = calculator.calculate(input);
	// CAGR is attached inside calculator.calculate() as of #5198 Phase 4.
	// Re-key breakdown array by classKey for dot-notation access:
	// calc.classes.CommonStock.value, calc.classes.ClassA.cagr, etc.
	if (result.breakdown) {
		result.classes = {};
		result.breakdown.forEach(function (classData) {
			if (classData.classKey) {
				result.classes[classData.classKey] = classData;
			}
		});
	}

	// Compute vested/unvested breakdown and attach to result for calc.* access
	const unvestedShares = calculateRestrictedShares(context, null);
	const ownedShares = Number(context.owned) || 0;
	const vestedSharesCount = Math.max(0, ownedShares - unvestedShares);
	const totalValue = result.totalValue || 0;
	const vestedFraction = ownedShares > 0 ? vestedSharesCount / ownedShares : 1;
	result.vestedShares = vestedSharesCount;
	result.unvestedShares = unvestedShares;
	result.vestedValue = totalValue * vestedFraction;
	result.unvestedValue = totalValue * (1 - vestedFraction);

	context.calc = result;

	// Merge calc fields onto context.user so formulas can use user.totalValue,
	// user.classes.CommonStock.value, etc. Only set keys that don't already exist.
	if (result && context.user) {
		Object.keys(result).forEach(function (key) {
			if (!(key in context.user)) {
				context.user[key] = result[key];
			}
		});
	}
}

/**
 * Get nested value from object using dot notation path
 * @param {Object} obj - Object to get value from
 * @param {string} path - Dot notation path (e.g., 'user.user_name')
 * @returns {*} Value at path or undefined
 */
function getNestedValue(obj, path) {
	if (!obj || !path) return undefined;
	return path.split('.').reduce((current, key) => current?.[key], obj);
}

/**
 * Sum events of a specific type within a date range
 * @param {Array} userEvents - User's events array
 * @param {Array} clientEvents - Client's events array (for event type lookup)
 * @param {string} eventType - Event type to filter by
 * @param {number} startDate - Start date (Unix timestamp)
 * @param {number} endDate - End date (Unix timestamp)
 * @returns {number} Sum of matching events
 */
function sumEventsInRange(userEvents, clientEvents, eventType, startDate, endDate) {
	if (!userEvents || !clientEvents) return 0;

	let sum = 0;
	userEvents.forEach((ue) => {
		const clientEvent = clientEvents.find((ce) => ce.nrid === ue.nrid);
		if (!clientEvent) return;

		const eventDate = Number(clientEvent.date);
		if (startDate && eventDate < startDate) return;
		if (endDate && eventDate > endDate) return;
		if (clientEvent.eventType !== eventType) return;

		sum += Number(ue.grossDollars) || 0;
	});

	return sum;
}

/**
 * Calculate tax basis for a shareholder
 * Tax Basis = Contributions + Income Allocations - Distributions
 * @param {Object} context - Shareholder context with events
 * @param {Object} params - Report parameters (dates)
 * @returns {number} Tax basis
 */
function calculateTaxBasis(context, params) {
	const { userEvents, clientEvents } = context;
	const { startDate, endDate } = params;

	const contributions = sumEventsInRange(userEvents, clientEvents, 'Contribution', null, endDate);
	const incomeAllocations = sumEventsInRange(userEvents, clientEvents, 'Gain (Loss) Accrual', null, endDate);
	const distributions = sumEventsInRange(userEvents, clientEvents, 'Distribution', null, endDate);
	const cashDividends = sumEventsInRange(userEvents, clientEvents, 'Cash Dividend', null, endDate);

	return contributions + incomeAllocations - distributions - cashDividends;
}

/**
 * Calculate ownership percentage
 * @param {Object} context - Shareholder context
 * @returns {number} Ownership percentage
 */
function calculateOwnershipPercent(context) {
	const owned = context.shares?.owned || 0;
	const totalShares = context.totalShares || 0;
	if (totalShares === 0) return 0;
	return (owned / totalShares) * 100;
}

/**
 * Calculate balance at a specific date (backwards calculation)
 * Starts from current balance and reverses transactions after the target date
 * @param {Object} context - Shareholder context
 * @param {number} asOfDate - Target date (Unix timestamp)
 * @returns {number} Balance at date
 */
function calculateBalanceAtDate(context, asOfDate) {
	const { currentBalance, transactions, userid } = context;
	if (!asOfDate) return currentBalance || 0;

	let adjustedBalance = currentBalance || 0;

	if (transactions && Array.isArray(transactions)) {
		transactions.forEach((tx) => {
			const txDate = Number(tx.date);
			if (txDate > asOfDate) {
				// Reverse this transaction
				const quantity = Number(tx.quantity) || 0;
				if (tx.receiver === userid) {
					adjustedBalance -= quantity; // Was added, so subtract
				} else if (tx.sender === userid) {
					adjustedBalance += quantity; // Was removed, so add back
				}
			}
		});
	}

	return adjustedBalance;
}

/**
 * Sum all shareholder balances for a share class
 * @param {Object} context - Share class context with shareholders
 * @returns {number} Total issued shares
 */
function sumShareholderBalances(context) {
	const { shareholders } = context;
	if (!shareholders) return 0;
	return shareholders.reduce((sum, s) => sum + (s.balance || 0), 0);
}

/**
 * Sum treasury account balances
 * @param {Object} context - Share class context with shareholders
 * @returns {number} Total treasury shares
 */
function sumTreasuryBalances(context) {
	const { shareholders } = context;
	if (!shareholders) return 0;
	return shareholders.filter((s) => s.isTreasury).reduce((sum, s) => sum + (s.balance || 0), 0);
}

/**
 * Count non-treasury accounts with positive balance
 * @param {Object} context - Share class context with shareholders
 * @returns {number} Count of active non-treasury accounts
 */
function countNonTreasuryAccounts(context) {
	const { shareholders } = context;
	if (!shareholders) return 0;
	return shareholders.filter((s) => !s.isTreasury && s.balance > 0).length;
}

/**
 * Calculate restricted (unvested) shares from vesting schedules
 * @param {Object} context - Shareholder context
 * @param {Object} params - Report parameters (may include asOfDate)
 * @returns {number} Number of restricted shares
 */
function calculateRestrictedShares(context, params) {
	const { userClient, shareClasses } = context;
	if (!userClient?.shares) return 0;

	const asOfDate = params?.asOfDate || Math.floor(Date.now() / 1000);
	let restrictedShares = 0;

	// Iterate through share classes
	const classes = shareClasses || Object.keys(userClient.shares);
	classes.forEach((shareClass) => {
		const vestingData = userClient.shares[shareClass]?.vesting;
		if (!vestingData) return;

		Object.values(vestingData).forEach((program) => {
			if (!program) return;

			// Schedule-based vesting
			if (program.schedule) {
				Object.entries(program.schedule).forEach(([vestDateStr, entry]) => {
					const vestTimestamp = Number(vestDateStr);
					if (vestTimestamp > asOfDate && entry?.amount) {
						if (!entry.posted && !entry.vested) {
							restrictedShares += Number(entry.amount) || 0;
						}
					}
				});
			}

			// Goal-based vesting
			if (program.goals) {
				Object.values(program.goals).forEach((goal) => {
					if (goal && !goal.vested && goal.amount) {
						restrictedShares += Number(goal.amount) || 0;
					}
				});
			}
		});
	});

	return restrictedShares;
}

/**
 * Calculate balance at a historic date using backwards calculation from transactions
 * Works with full transaction history from clientData
 * @param {Object} context - Shareholder context
 * @param {Object} params - Report parameters with asOfDate
 * @returns {number} Balance at the specified date
 */
function calculateBalanceAtHistoricDate(context, params) {
	const { userClient, userid, clientData, shareClass } = context;
	const asOfDate = params?.asOfDate;

	if (!userClient?.shares || !shareClass) return 0;

	// Get current balance
	const currentBalance = Number(String(userClient.shares[shareClass]?.owned || '0').replace(/[^.\d]/g, '')) || 0;

	if (!asOfDate) return currentBalance;

	let adjustedBalance = currentBalance;
	const equity = clientData?.equity?.[shareClass];
	if (!equity?.transactions?.length) return adjustedBalance;

	const shareholderIdLower = userid.toLowerCase();

	// Find transactions AFTER asOfDate involving this shareholder
	equity.transactions.forEach((tx) => {
		const txDate = Number(tx.date || 0);
		if (txDate <= asOfDate) return;

		const receiver = String(tx.receiver || '').toLowerCase();
		const sender = String(tx.sender || '').toLowerCase();
		const qty = Number(String(tx.quantity || '0').replace(/[^.\d]/g, '')) || 0;

		if (receiver === shareholderIdLower) {
			adjustedBalance -= qty; // Received after date, subtract
		}
		if (sender === shareholderIdLower) {
			adjustedBalance += qty; // Sent after date, add back
		}
	});

	return Math.max(0, adjustedBalance);
}

/**
 * Calculate total value of holdings (shares × valuation price)
 * @param {Object} context - Shareholder context
 * @param {Object} params - Report parameters
 * @returns {number} Total value
 */
function calculateTotalValue(context, params) {
	const { userClient, clientData, shareClasses } = context;
	if (!userClient?.shares || !clientData?.equity) return 0;

	let totalValue = 0;
	const classes = shareClasses || Object.keys(userClient.shares);

	classes.forEach((shareClass) => {
		const owned = Number(userClient.shares[shareClass]?.owned) || 0;
		if (owned <= 0) return;

		const equity = clientData.equity[shareClass];
		if (!equity?.valuations?.length) return;

		// Get most recent valuation (or valuation at asOfDate if provided)
		let valuation;
		if (params?.asOfDate) {
			valuation = equity.valuations.filter((v) => Number(v.date) <= params.asOfDate).sort((a, b) => Number(b.date) - Number(a.date))[0];
		} else {
			valuation = equity.valuations.sort((a, b) => Number(b.date) - Number(a.date))[0];
		}

		if (valuation) {
			const price = Number(valuation.sharePrice) || 0;
			totalValue += owned * price;
		}
	});

	return totalValue;
}

/**
 * Calculate capital account (fund investor model)
 * Capital Account = Contributions - Distributions + Income Allocations + Gain/Loss Accruals
 * @param {Object} context - Shareholder context
 * @param {Object} params - Report parameters
 * @returns {number} Capital account balance
 */
function calculateCapitalAccount(context, params) {
	const { userEvents, clientEvents } = context;
	const { startDate, endDate } = params;

	const contributions = sumEventsInRange(userEvents, clientEvents, 'Contribution', null, endDate);
	const distributions = sumEventsInRange(userEvents, clientEvents, 'Distribution', null, endDate);
	const incomeAllocations = sumEventsInRange(userEvents, clientEvents, 'Gain (Loss) Accrual', null, endDate);
	const returnOfCapital = sumEventsInRange(userEvents, clientEvents, 'Return of Capital', null, endDate);

	return contributions - distributions + incomeAllocations - returnOfCapital;
}

/**
 * Get shareholder's allocation from a specific event
 * @param {Object} context - Context with userEvents and target event info
 * @param {Object} params - Parameters with eventNrid
 * @returns {number} Allocation amount
 */
function getEventAllocation(context, params) {
	const { userEvents } = context;
	const { eventNrid } = params;

	if (!userEvents || !eventNrid) return 0;

	const userEvent = userEvents.find((e) => e.nrid === eventNrid);
	return Number(userEvent?.grossDollars) || 0;
}

/**
 * Calculate authorized but unissued shares for a share class
 * @param {Object} context - Share class context
 * @returns {number|null} Authorized unissued or null if no authorized value
 */
function calculateAuthorizedUnissued(context) {
	const { classConfig, shareholders } = context;

	const authorized = Number(String(classConfig?.authorized || '0').replace(/[^.\d]/g, '')) || 0;
	if (authorized <= 0) return null;

	const issued = shareholders?.reduce((sum, s) => sum + (s.balance || 0), 0) || 0;
	return authorized - issued;
}

/**
 * Calculate outstanding shares (issued - treasury)
 * @param {Object} context - Share class context
 * @returns {number} Outstanding shares
 */
function calculateOutstandingShares(context) {
	const { shareholders } = context;
	if (!shareholders) return 0;

	const issued = shareholders.reduce((sum, s) => sum + (s.balance || 0), 0);
	const treasury = shareholders.filter((s) => s.isTreasury).reduce((sum, s) => sum + (s.balance || 0), 0);

	return issued - treasury;
}

/**
 * Sum restricted (unvested) shares across all non-treasury shareholders in a share class
 * Used for BW Equity Rollup "Outstanding Restricted" column
 * @param {Object} context - Share class context with shareholders, capTableData, clientData
 * @param {Object} params - Report parameters (may include asOfDate)
 * @returns {number} Total restricted shares for this share class
 */
function sumOutstandingRestricted(context, params) {
	const { shareholders, shareClass, capTableData, clientData } = context;
	if (!shareholders || !capTableData || !shareClass) return 0;

	const asOfDate = params?.asOfDate || Math.floor(Date.now() / 1000);
	let totalRestricted = 0;

	shareholders.forEach((s) => {
		if (s.isTreasury || s.balance <= 0) return;

		// Find the full user record in capTableData to access vesting data
		const user = capTableData.find((u) => u.userid === s.userid);
		if (!user) return;

		const client = Object.keys(user.clients || {})[0];
		const userClient = user.clients?.[client];
		if (!userClient?.shares?.[shareClass]?.vesting) return;

		const vestingData = userClient.shares[shareClass].vesting;
		let restricted = 0;

		Object.values(vestingData).forEach((program) => {
			if (!program) return;
			if (program.schedule) {
				Object.entries(program.schedule).forEach(([vestDateStr, entry]) => {
					if (Number(vestDateStr) > asOfDate && entry?.amount && !entry.posted && !entry.vested) {
						restricted += Number(entry.amount) || 0;
					}
				});
			}
			if (program.goals) {
				Object.values(program.goals).forEach((goal) => {
					if (goal && !goal.vested && goal.amount) {
						restricted += Number(goal.amount) || 0;
					}
				});
			}
		});

		// Cap restricted at balance
		totalRestricted += Math.min(restricted, s.balance);
	});

	return totalRestricted;
}

/**
 * Sum non-restricted outstanding shares across all non-treasury shareholders in a share class
 * Used for BW Equity Rollup "Outstanding Non Restricted" column
 * @param {Object} context - Share class context
 * @param {Object} params - Report parameters
 * @returns {number} Total non-restricted outstanding shares
 */
function sumOutstandingNonRestricted(context, params) {
	const outstanding = calculateOutstandingShares(context);
	const restricted = sumOutstandingRestricted(context, params);
	return outstanding - restricted;
}

/**
 * Sum events by type within date range, returning count instead of sum
 * @param {Array} userEvents - User's events
 * @param {Array} clientEvents - Client's events
 * @param {string} eventType - Event type to filter
 * @param {number} startDate - Start date
 * @param {number} endDate - End date
 * @returns {number} Count of matching events
 */
function countEventsInRange(userEvents, clientEvents, eventType, startDate, endDate) {
	if (!userEvents || !clientEvents) return 0;

	let count = 0;
	userEvents.forEach((ue) => {
		const clientEvent = clientEvents.find((ce) => ce.nrid === ue.nrid);
		if (!clientEvent) return;

		const eventDate = Number(clientEvent.date);
		if (startDate && eventDate < startDate) return;
		if (endDate && eventDate > endDate) return;
		if (eventType && clientEvent.eventType !== eventType) return;

		count++;
	});

	return count;
}

/**
 * Get the latest event date for a shareholder
 * @param {Object} context - Shareholder context
 * @param {Object} params - Parameters with optional eventType filter
 * @returns {number|null} Unix timestamp of latest event or null
 */
function getLatestEventDate(context, params) {
	const { userEvents, clientEvents } = context;
	const { eventType } = params || {};

	if (!userEvents || !clientEvents) return null;

	let latestDate = null;
	userEvents.forEach((ue) => {
		const clientEvent = clientEvents.find((ce) => ce.nrid === ue.nrid);
		if (!clientEvent) return;
		if (eventType && clientEvent.eventType !== eventType) return;

		const eventDate = Number(clientEvent.date);
		if (!latestDate || eventDate > latestDate) {
			latestDate = eventDate;
		}
	});

	return latestDate;
}

/**
 * Parse an address string into components: Address 1, Address 2, City, State, Postal Code
 * @param {string} addressString - Full address string
 * @returns {Object} { address1, address2, city, state, postalCode }
 */
function parseAddressComponents(addressString) {
	if (!addressString || typeof addressString !== 'string') {
		return { address1: '', address2: '', city: '', state: '', postalCode: '' };
	}

	// Clean up special characters and normalize
	let address = addressString.replace(/#+/g, ' ').replace(/\n/g, ',').replace(/\r/g, '').replace(/;/g, ',').replace(/\s+/g, ' ').trim();

	address = address.replace(/,\s*,/g, ',');

	// Extract postal code (5-digit or 5+4 format)
	const zipPattern = /\b(\d{5}(?:-\d{4})?)\b/;
	const zipMatch = address.match(zipPattern);
	let postalCode = '';
	let addressWithoutZip = address;

	if (zipMatch) {
		postalCode = zipMatch[1];
		const zipMatchIndex = zipMatch.index;
		const zipMatchLength = zipMatch[0].length;

		// Check for extended format like "19009-630041"
		const afterZipIndex = zipMatchIndex + zipMatchLength;
		const afterZip = address.substring(afterZipIndex);
		const extendedZipMatch = afterZip.match(/^\s*-\d+/);

		if (extendedZipMatch) {
			const beforeZip = address.substring(0, zipMatchIndex);
			const afterExtendedZip = address.substring(afterZipIndex + extendedZipMatch[0].length);
			addressWithoutZip = (beforeZip + afterExtendedZip).trim();
		} else {
			addressWithoutZip = address.replace(zipPattern, '').trim();
		}

		// Clean up any remaining dash-suffix patterns
		addressWithoutZip = addressWithoutZip.replace(/\s+-\d+/g, '');
		addressWithoutZip = addressWithoutZip.replace(/-\d+\b/g, '');
		addressWithoutZip = addressWithoutZip.replace(/[,\s-]+$/, '').trim();
		addressWithoutZip = addressWithoutZip.replace(/^[-\s]+/, '').trim();
	}

	// Extract state (2-letter state codes) from the end
	let state = '';
	const statePatternCommaEnd = /,\s*\b([A-Z]{2})\b\s*$/;
	let stateMatch = addressWithoutZip.match(statePatternCommaEnd);

	if (stateMatch) {
		state = stateMatch[1].toUpperCase();
		addressWithoutZip = addressWithoutZip.replace(/,\s*[A-Z]{2}\s*$/, '').trim();
	} else {
		const statePatternSpaceEnd = /\s+\b([A-Z]{2})\b\s*$/;
		stateMatch = addressWithoutZip.match(statePatternSpaceEnd);
		if (stateMatch) {
			state = stateMatch[1].toUpperCase();
			addressWithoutZip = addressWithoutZip.replace(/\s+[A-Z]{2}\s*$/, '').trim();
		}
	}

	// Split remaining address by commas
	const parts = addressWithoutZip
		.split(',')
		.map((p) => p.trim())
		.filter((p) => p.length > 0);

	let city = '';
	let addressParts = [];

	if (parts.length === 0) {
		// No commas - need to identify where street ends and city begins
		const words = addressWithoutZip.split(/\s+/);
		const streetSuffixes = [
			'STREET',
			'ST',
			'AVENUE',
			'AVE',
			'ROAD',
			'RD',
			'DRIVE',
			'DR',
			'LANE',
			'LN',
			'BOULEVARD',
			'BLVD',
			'COURT',
			'CT',
			'CIRCLE',
			'CIR',
			'PLACE',
			'PL',
			'PARKWAY',
			'PKWY',
			'WAY',
			'TERRACE',
			'TER',
			'TRAIL',
			'TRL',
			'HIGHWAY',
			'HWY',
		];

		const lastWord = words.length > 0 ? words[words.length - 1].toUpperCase() : '';
		const isStreetSuffix = streetSuffixes.includes(lastWord);

		if (words.length <= 2 || isStreetSuffix) {
			addressParts = [addressWithoutZip];
		} else {
			let foundCity = false;
			for (let i = Math.min(3, words.length - 1); i >= 1; i--) {
				const candidateCityWords = words.slice(-i);
				const candidateStreetWords = words.slice(0, -i);
				const candidateCity = candidateCityWords.join(' ');
				const candidateStreet = candidateStreetWords.join(' ');

				const streetStartsWithNumber = candidateStreetWords.length > 0 && /^\d/.test(candidateStreetWords[0]);
				const streetHasSuffix = candidateStreetWords.some((w) => streetSuffixes.includes(w.toUpperCase()));
				const cityWordsLookValid = candidateCityWords.every((w) => w.length > 0 && !/^\d/.test(w));

				if (streetStartsWithNumber && candidateStreetWords.length >= 2 && cityWordsLookValid && !streetHasSuffix) {
					city = candidateCity;
					addressParts = [candidateStreet];
					foundCity = true;
					break;
				}
			}
			if (!foundCity) {
				addressParts = [addressWithoutZip];
			}
		}
	} else if (parts.length === 2) {
		addressParts = [parts[0]];
		city = parts[1];
	} else {
		city = parts[parts.length - 1];
		addressParts = parts.slice(0, -1);
	}

	let address1 = '';
	let address2 = '';

	if (addressParts.length === 0) {
		address1 = addressWithoutZip;
	} else if (addressParts.length === 1) {
		address1 = addressParts[0];
	} else {
		address1 = addressParts[0];
		address2 = addressParts.slice(1).join(', ');
	}

	return {
		address1: address1.trim(),
		address2: address2.trim(),
		city: city.trim(),
		state: state,
		postalCode: postalCode,
	};
}

/**
 * Evaluate simple arithmetic expression using resolved values
 * @param {string} expression - Expression like 'issued-treasury'
 * @param {Object} resolvedValues - Object with resolved column values
 * @returns {number} Result of expression
 */
function evaluateExpression(expression, resolvedValues) {
	// Simple parser for basic arithmetic: a+b, a-b, a*b, a/b
	const parts = expression.split(/([+\-*/])/);
	if (parts.length < 3) return resolvedValues[expression] || 0;

	let result = Number(resolvedValues[parts[0].trim()]) || 0;
	for (let i = 1; i < parts.length; i += 2) {
		const operator = parts[i];
		const operand = Number(resolvedValues[parts[i + 1]?.trim()]) || 0;
		switch (operator) {
			case '+':
				result += operand;
				break;
			case '-':
				result -= operand;
				break;
			case '*':
				result *= operand;
				break;
			case '/':
				result = operand !== 0 ? result / operand : 0;
				break;
		}
	}
	return result;
}

/**
 * Column resolvers by source type
 */
const columnResolvers = {
	/**
	 * Direct field access: 'user.user_name', 'classConfig.authorized'
	 */
	direct: (path, context) => {
		// Lazily compute calculator results when calc.* or user.* paths are accessed
		// For user.*, this merges calc fields onto context.user (no-op if already computed)
		if (path.startsWith('calc.') || path.startsWith('user.')) ensureCalcResult(context);
		return getNestedValue(context, path);
	},

	/**
	 * Report parameter access: 'param:year', 'param:shareholder'
	 */
	param: (paramName, context, params) => params?.[paramName] ?? null,

	/**
	 * Event aggregation: 'aggregate:Contribution'
	 */
	aggregate: (eventType, context, params) => {
		const { userEvents, clientEvents } = context;
		return sumEventsInRange(userEvents, clientEvents, eventType, params.startDate, params.endDate);
	},

	/**
	 * Predefined calculator: 'calculator:taxBasis'
	 */
	calculator: (calculatorName, context, params) => {
		const calculators = {
			// Tax and financial calculations — delegate to ensureCalcResult to respect
			// the client's assigned calculation method (#4854)
			taxBasis: () => {
				ensureCalcResult(context);
				if (context.calc?.totalTaxBasis !== undefined) return context.calc.totalTaxBasis;
				return calculateTaxBasis(context, params);
			},
			capitalAccount: () => {
				ensureCalcResult(context);
				if (context.calc?.totalCapitalAccount !== undefined) return context.calc.totalCapitalAccount;
				return calculateCapitalAccount(context, params);
			},
			totalValue: () => {
				ensureCalcResult(context);
				return context.calc?.totalValue ?? 0;
			},

			// Ownership calculations
			ownershipPercent: () => calculateOwnershipPercent(context),
			balanceAtDate: () => calculateBalanceAtDate(context, params?.asOfDate),
			balanceAtHistoricDate: () => calculateBalanceAtHistoricDate(context, params),
			restrictedShares: () => calculateRestrictedShares(context, params),
			vestedShares: () => {
				ensureCalcResult(context);
				return context.calc?.vestedShares ?? 0;
			},

			// Share class aggregations
			sumBalances: () => sumShareholderBalances(context),
			sumTreasury: () => sumTreasuryBalances(context),
			countNonTreasury: () => countNonTreasuryAccounts(context),
			authorizedUnissued: () => calculateAuthorizedUnissued(context),
			outstandingShares: () => calculateOutstandingShares(context),
			outstandingRestricted: () => sumOutstandingRestricted(context, params),
			outstandingNonRestricted: () => sumOutstandingNonRestricted(context, params),

			// Event calculations
			eventAllocation: () => getEventAllocation(context, params),
			latestEventDate: () => getLatestEventDate(context, params),
			eventCount: () => countEventsInRange(context.userEvents, context.clientEvents, params?.eventType, params?.startDate, params?.endDate),

			// Dividend report calculations
			vendorCodeFormatted: () => {
				const userClient = context.receiverClient || context.userClient;
				const idNumber = userClient?.idNumber;
				if (idNumber && idNumber !== 'NaN' && String(idNumber).toLowerCase() !== 'nan') {
					return '\u200B' + String(idNumber);
				}
				return '';
			},
			allocationPercentage: () => {
				const allocation = context.allocationAmount || 0;
				const total = context.eventGrossTotal || 0;
				if (total === 0) return 0;
				return (allocation / total) * 100;
			},
			perShareAmount: () => {
				const allocation = context.allocationAmount || 0;
				const shares = context.sharesOwned || 0;
				if (shares === 0) return 0;
				return allocation / shares;
			},
			vesting: () => {
				const userClient = context.receiverClient || context.userClient;
				return userClient?.vesting ? 'True' : '';
			},

			// Vendor code split into two parts (for Stock Listing report)
			// Vendor codes are typically in format "VENDORCODE-LINENUMBER"
			// Sources: transaction.vendorCode, receiverClient.idNumber, or extracted from user name
			vendorCodeOnly: () => {
				// Try to get vendor code from multiple sources
				let vendorCode = context.transaction?.vendorCode || context.vendorCode || '';

				// If not in transaction, try receiverClient.idNumber
				if (!vendorCode) {
					const userClient = context.receiverClient || context.userClient;
					vendorCode = userClient?.idNumber || '';
				}

				// If still empty, try to extract from receiver's name (format: "Name (123-45)")
				if (!vendorCode && context.receiver?.user_name) {
					const match = context.receiver.user_name.match(/\(([^)]+)\)/);
					if (match) {
						vendorCode = match[1].replace(/\s*-\s*/g, '-').trim();
					}
				}

				if (!vendorCode) return '';
				const parts = String(vendorCode).split('-');
				return parts[0] || '';
			},
			// Sender's shareholder number (reconciliation report): same logic as vendorCodeOnly but for sender
			senderVendorCodeOnly: () => {
				const userClient = context.senderClient;
				const user = context.sender;
				let vendorCode = userClient?.idNumber || '';

				if (!vendorCode && user?.user_name) {
					const match = user.user_name.match(/\(([^)]+)\)/);
					if (match) {
						vendorCode = match[1].replace(/\s*-\s*/g, '-').trim();
					}
				}

				if (!vendorCode && userClient?.tags && userClient.tags.length > 0) {
					const ffmTag = userClient.tags.join('');
					const ffmMatch = ffmTag.match(/^F(\d)(\d)/);
					if (ffmMatch) {
						vendorCode = `F${ffmMatch[1]}${ffmMatch[2]}`;
					}
				}

				if (!vendorCode) return '';
				const parts = String(vendorCode).split('-');
				return parts[0] || '';
			},
			lineNumber: () => {
				// Try to get vendor code from multiple sources
				let vendorCode = context.transaction?.vendorCode || context.vendorCode || '';

				// If not in transaction, try receiverClient.idNumber
				if (!vendorCode) {
					const userClient = context.receiverClient || context.userClient;
					vendorCode = userClient?.idNumber || '';
				}

				// If still empty, try to extract from receiver's name (format: "Name (123-45)")
				if (!vendorCode && context.receiver?.user_name) {
					const match = context.receiver.user_name.match(/\(([^)]+)\)/);
					if (match) {
						vendorCode = match[1].replace(/\s*-\s*/g, '-').trim();
					}
				}

				if (!vendorCode) return '';
				const parts = String(vendorCode).split('-');
				return parts[1] || '';
			},

			// Address parsing calculators
			address1: () => {
				const userClient = context.receiverClient || context.userClient;
				const addressString = userClient?.address || '';
				return parseAddressComponents(addressString).address1;
			},
			address2: () => {
				const userClient = context.receiverClient || context.userClient;
				const addressString = userClient?.address || '';
				return parseAddressComponents(addressString).address2;
			},
			city: () => {
				const userClient = context.receiverClient || context.userClient;
				const addressString = userClient?.address || '';
				return parseAddressComponents(addressString).city;
			},
			state: () => {
				const userClient = context.receiverClient || context.userClient;
				const addressString = userClient?.address || '';
				return parseAddressComponents(addressString).state;
			},
			postalCode: () => {
				const userClient = context.receiverClient || context.userClient;
				const addressString = userClient?.address || '';
				return parseAddressComponents(addressString).postalCode;
			},

			// Last seen - extract timestamp from lastActive object
			lastSeen: () => {
				const userClient = context.receiverClient || context.userClient;
				const lastActive = userClient?.lastActive;
				if (!lastActive?.timestamp) return null;
				return Number(lastActive.timestamp);
			},
			// Invited at - convert ms timestamp to seconds
			invitedAt: () => {
				const userClient = context.receiverClient || context.userClient;
				const invitedAt = userClient?.invited_at;
				if (!invitedAt) return null;
				return Number(invitedAt) / 1000;
			},
			// Alias count - number of alias accounts this user has
			aliasCount: () => {
				const userClient = context.receiverClient || context.userClient;
				const alias = userClient?.alias;
				return alias ? alias.length : 0;
			},
			// Alias string - pipe-separated list of alias email addresses
			aliasString: () => {
				const userClient = context.receiverClient || context.userClient;
				const alias = userClient?.alias;
				if (!alias || alias.length === 0) return '';
				const capTableData = context.capTableData;
				return alias
					.map((a) => {
						const aliasUser = capTableData?.find((u) => u.userid === a);
						return aliasUser ? aliasUser.user_name + ' (' + a + ')' : a;
					})
					.join(' | ');
			},
			// Observer count - number of users who have this user in their alias list
			observerCount: () => {
				const capTableData = context.capTableData;
				const userid = context.userid || context.user?.userid;
				if (!capTableData || !userid) return 0;
				const clientKey = context.clientKey || (typeof client !== 'undefined' ? client : '');
				let count = 0;
				capTableData.forEach((u) => {
					const uc = u.clients?.[clientKey];
					if (uc?.alias && uc.alias.includes(userid)) count++;
				});
				return count;
			},
			// Observer string - pipe-separated list of observers (name + email)
			observerString: () => {
				const capTableData = context.capTableData;
				const userid = context.userid || context.user?.userid;
				if (!capTableData || !userid) return '';
				const clientKey = context.clientKey || (typeof client !== 'undefined' ? client : '');
				const observers = [];
				capTableData.forEach((u) => {
					const uc = u.clients?.[clientKey];
					if (uc?.alias && uc.alias.includes(userid)) {
						observers.push(u.user_name + ' (' + u.userid + ')');
					}
				});
				return observers.join(' | ');
			},

			// Document count
			docCount: () => {
				const userClient = context.receiverClient || context.userClient;
				const docs = userClient?.documents;
				return docs && docs.length > 0 ? docs.length : 0;
			},
			// Document string - pipe-separated list of document names with descriptions
			docString: () => {
				const userClient = context.receiverClient || context.userClient;
				const docs = userClient?.documents;
				if (!docs || docs.length === 0) return '';
				return docs.map((doc) => doc.name + (doc.description ? ' (' + doc.description + ')' : '')).join(' | ');
			},

			// Change status calculator for New & Updated Shareholders report
			// Determines "New" vs "Updated" based on createdAt/lastUpdated comparison
			changeStatus: () => {
				const userClient = context.receiverClient || context.userClient;
				const createdAt = userClient?.createdAt;
				const lastUpdated = userClient?.lastUpdated;

				if (lastUpdated && createdAt) {
					const createdAtDate = new Date(createdAt);
					const lastUpdatedDate = new Date(lastUpdated);
					return lastUpdatedDate > createdAtDate ? 'Updated' : 'New';
				} else if (lastUpdated) {
					return 'Updated';
				} else if (createdAt) {
					return 'New';
				}
				return '';
			},

			// Vendor code extracted from name or idNumber
			// Extracts the part before the hyphen (shareholder number)
			vendorCodeExtracted: () => {
				const userClient = context.receiverClient || context.userClient;
				const user = context.receiver || context.user;

				// First try idNumber
				let vendorCode = userClient?.idNumber || '';

				// If not found, try to extract from user name (format: "Name (123-45)")
				if (!vendorCode && user?.user_name) {
					const match = user.user_name.match(/\(([^)]+)\)/);
					if (match) {
						vendorCode = match[1].replace(/\s*-\s*/g, '-').trim();
					}
				}

				// If not found, try tags for Asplundh FFM format
				if (!vendorCode && userClient?.tags && userClient.tags.length > 0) {
					const ffmTag = userClient.tags.join('');
					const ffmMatch = ffmTag.match(/^F(\d)(\d)/);
					if (ffmMatch) {
						vendorCode = `F${ffmMatch[1]}${ffmMatch[2]}`;
					}
				}

				if (!vendorCode) return '';

				// Return just the part before hyphen (shareholder number)
				const parts = String(vendorCode).split('-');
				return parts[0] || '';
			},

			// Created/Updated timestamp formatted
			createdAtFormatted: () => {
				const userClient = context.receiverClient || context.userClient;
				const createdAt = userClient?.createdAt;
				if (!createdAt) return '';
				// Format as MM/DD/YYYY h:mm A
				const date = new Date(createdAt);
				if (isNaN(date.getTime())) return '';
				const month = String(date.getMonth() + 1).padStart(2, '0');
				const day = String(date.getDate()).padStart(2, '0');
				const year = date.getFullYear();
				const hours = date.getHours();
				const minutes = String(date.getMinutes()).padStart(2, '0');
				const ampm = hours >= 12 ? 'PM' : 'AM';
				const hour12 = hours % 12 || 12;
				return `${month}/${day}/${year} ${hour12}:${minutes} ${ampm}`;
			},
			lastUpdatedFormatted: () => {
				const userClient = context.receiverClient || context.userClient;
				const lastUpdated = userClient?.lastUpdated;
				if (!lastUpdated) return '';
				// Format as MM/DD/YYYY h:mm A
				const date = new Date(lastUpdated);
				if (isNaN(date.getTime())) return '';
				const month = String(date.getMonth() + 1).padStart(2, '0');
				const day = String(date.getDate()).padStart(2, '0');
				const year = date.getFullYear();
				const hours = date.getHours();
				const minutes = String(date.getMinutes()).padStart(2, '0');
				const ampm = hours >= 12 ? 'PM' : 'AM';
				const hour12 = hours % 12 || 12;
				return `${month}/${day}/${year} ${hour12}:${minutes} ${ampm}`;
			},
		};
		return calculators[calculatorName]?.() ?? null;
	},

	/**
	 * Arithmetic expression: 'expression:issued-treasury'
	 * Now uses FormulaEngine for full formula support
	 */
	expression: (expr, context) => {
		// Use FormulaEngine for advanced formulas
		return FormulaEngine.evaluate(expr, context);
	},

	/**
	 * User-defined formula: 'formula:IF(owned > 0, owned / totalShares * 100, 0)'
	 */
	formula: (formulaExpr, context) => {
		return FormulaEngine.evaluate(formulaExpr, context);
	},

	/**
	 * Classification check: 'classifier:treasury'
	 */
	classifier: (classifierKey, context) => {
		return context.classifications?.[classifierKey] || false;
	},

	/**
	 * Manual custom column: 'custom:{key}' — reads from userClient['ctcc_' + key]
	 */
	custom: (key, context) => {
		const val = context.userClient?.['ctcc_' + key];
		return val !== undefined && val !== null ? val : '';
	},
};

/**
 * Map a custom column's configured format (e.g. from cap_table_custom_columns)
 * to the display-type vocabulary consumers of resolveColumnValue expect
 * (HeadlineFormatter's variableTypes, VariableProvider's types map, etc.).
 * @param {string} format - Column format from config (e.g., 'currency', 'number', 'percentage')
 * @returns {string} One of 'currency' | 'number' | 'percentage' | 'multiplier' | 'raw'
 */
export function mapCustomColumnFormatToType(format) {
	if (!format) return 'raw';
	const f = String(format).toLowerCase();
	if (f === 'currency' || f === 'dollars' || f === 'cents') return 'currency';
	if (f === 'number' || f === 'integer' || f === 'decimal') return 'number';
	if (f === 'percentage' || f === 'percent') return 'percentage';
	if (f === 'multiplier') return 'multiplier';
	return 'raw';
}

/**
 * Resolve a column value based on its source definition
 * @param {Object} column - Column definition with source
 * @param {Object} context - Data context for resolution
 * @param {Object} params - Report parameters
 * @returns {*} Resolved value
 */
export function resolveColumnValue(column, context, params) {
	const { source } = column;
	if (!source) return null;

	// Parse source type and value
	const colonIndex = source.indexOf(':');
	if (colonIndex === -1) {
		// Auto-detect formula: if source contains math operators with identifiers, treat as formula
		if (/[+\-*/]/.test(source) && /[a-zA-Z]/.test(source)) {
			return columnResolvers.formula(source, context);
		}
		// Direct field access (no prefix)
		return columnResolvers.direct(source, context);
	}

	const sourceType = source.substring(0, colonIndex);
	const sourceValue = source.substring(colonIndex + 1);

	const resolver = columnResolvers[sourceType];
	if (!resolver) {
		console.warn(`Unknown source type: ${sourceType}`);
		return null;
	}

	return resolver(sourceValue, context, params);
}

export { ensureCalcResult };

export const ColumnResolvers = {
	resolveColumnValue,
	mapCustomColumnFormatToType,
	columnResolvers,
	ensureCalcResult,
	getNestedValue,
	evaluateExpression,
	sumEventsInRange,
	countEventsInRange,
	getEventAllocation,
	getLatestEventDate,
	calculateTaxBasis,
	calculateCapitalAccount,
	calculateTotalValue,
	calculateOwnershipPercent,
	calculateBalanceAtDate,
	calculateBalanceAtHistoricDate,
	calculateRestrictedShares,
	sumShareholderBalances,
	sumTreasuryBalances,
	countNonTreasuryAccounts,
	calculateAuthorizedUnissued,
	calculateOutstandingShares,
	sumOutstandingRestricted,
	sumOutstandingNonRestricted,
	parseAddressComponents,
};
